import { Component, inject, signal, OnInit, computed, effect } from '@angular/core';
import * as groupActions from '../../../../state/actions/group.actions';
import { toSignal } from '@angular/core/rxjs-interop';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { NGXLogger } from 'ngx-logger';
import { map, Observable, take } from 'rxjs';
import { Group } from '../../../../models/group.model';
import { Product } from '../../../../models/product.model';
import { UserModel } from '../../../../models/user.model';
import { ProductService } from '../../../../services/product.service';
import { StripeService } from '../../../../services/stripe.service';
import { loadStripe, Stripe, StripeElements } from '@stripe/stripe-js';
import { environment } from '../../../../../environments/environment';
import { AppState } from '../../../../state/appstate';
import * as groupSelector from '../../../../state/selectors/group.selector';
import { BtnBackComponent } from '../../../components/buttons/btn-back/btn-back.component';
import { BtnIconComponent } from '../../../components/buttons/btn-icon/btn-icon.component';
import { LoadingContainerComponent } from '../../../components/loaders/loading-container/loading-container.component';
import { UpperCasePipe } from '@angular/common';
import { BtnFabComponent } from '../../../components/buttons/btn-fab/btn-fab.component';
import { BtnSubmitComponent } from '../../../components/buttons/btn-submit/btn-submit.component';

@Component({
  selector: 'app-subscription',
  standalone: true,
  imports: [
    BtnIconComponent,
    FormsModule,
    ReactiveFormsModule,
    BtnBackComponent,
    LoadingContainerComponent,
    UpperCasePipe,
    BtnFabComponent,
    BtnSubmitComponent,
  ],
  templateUrl: './subscription.component.html',
  styleUrl: './subscription.component.css',
  providers: [],
})
export class SubscriptionComponent implements OnInit {
  private functions = inject(Functions);
  private stripeService = inject(StripeService);
  private productService = inject(ProductService);
  private router = inject(Router);
  store: Store<AppState> = inject(Store<AppState>);
  route: ActivatedRoute = inject(ActivatedRoute);

  stripe!: Stripe | null;
  elements!: StripeElements;
  clientSecret = signal<string | null>(null);

  error = signal<string | null>(null);
  loadingPayment = signal<boolean>(false);
  success = signal<boolean>(false);
  processing = false;
  showPaymentModal = signal<boolean>(false);

  promoCode = signal<string>('');
  promoCodeApplied = signal<boolean>(false);
  validatingPromoCode = signal<boolean>(false);
  promoCodeError = signal<string | null>(null);
  appliedCouponDetails = signal<any>(null);

  hasExistingSubscription = false;
  useExistingCard = false;
  existingSubscriptionId: string | null = null;

  logger: NGXLogger = inject(NGXLogger);

  selectedProductIds: string[] = [];

  groupId = toSignal(
    this.route.paramMap.pipe(map((params) => params.get('id'))),
    { initialValue: null },
  );

  studentCount = 25;

  groupCurrency = signal<string>('usd');
  groupType = signal<string | undefined>(undefined);

  getGroupProducts(): Product[] {
    return this.groupType() === 'church'
      ? this.productService.churchProducts()
      : this.productService.schoolProducts();
  }

  readonly loading = this.productService.loading;

  billingInterval = signal<'month' | 'year'>('month');
  
  getFreeSeatsCount(): number {
    const seatsProduct = this.getSeatsProduct();
    if (!seatsProduct || !seatsProduct.prices || seatsProduct.prices.length === 0) {
      return this.groupType() === 'church' ? 100 : (environment.STRIPE_SCHOOL_FREE_SEATS || 25);
    }
    const currency = (this.groupCurrency() || 'usd').toLowerCase();
    const selectedInterval = this.billingInterval();
    let price = seatsProduct.prices.find(
      (pr) => pr.active && 
              pr.currency.toLowerCase() === currency && 
              pr.recurring?.interval === selectedInterval
    );
    if (!price) {
      price = seatsProduct.prices[0];
    }

    if (price && price.tiers && price.tiers.length > 0) {
      let freeSeats = 0;
      for (const tier of price.tiers) {
        const isFree = (!tier.unit_amount || tier.unit_amount === 0) && (!tier.flat_amount || tier.flat_amount === 0);
        if (isFree && tier.up_to !== null) {
          freeSeats = tier.up_to;
        } else if (!isFree) {
          break;
        }
      }
      if (freeSeats > 0) {
        return freeSeats;
      }
    }
    
    return this.groupType() === 'church' ? 100 : (environment.STRIPE_SCHOOL_FREE_SEATS || 25);
  }

  readonly products = computed(() => {
    const currency = (this.groupCurrency() || 'usd').toLowerCase();
    const selectedInterval = this.billingInterval();
    return this.getGroupProducts()
      .map(p => {
        let filteredPrices = (p.prices || []).filter(
          (price) => price.active && price.currency.toLowerCase() === currency
        );
        // Fallback to USD price if matching currency doesn't exist
        if (filteredPrices.length === 0 && currency !== 'usd') {
          filteredPrices = (p.prices || []).filter(
            (price) => price.active && price.currency.toLowerCase() === 'usd'
          );
        }
        // Filter prices to match chosen billing interval (yearly vs monthly)
        let intervalPrices = filteredPrices.filter(
          (price) => price.recurring?.interval === selectedInterval
        );
        // Fallback if a product only has a monthly price (e.g. AI credits)
        if (intervalPrices.length === 0) {
          intervalPrices = filteredPrices;
        }
        return {
          ...p,
          prices: intervalPrices
        };
      })
      .filter((p) => p.prices && p.prices.length > 0)
      .sort((a, b) => {
        const aIsSeats = a.metadata?.productType === 'seats' || a.id === (environment.STRIPE_SCHOOL_SEATS_PRICE_ID || 'prod_TQqEowVFMUF4i0');
        const bIsSeats = b.metadata?.productType === 'seats' || b.id === (environment.STRIPE_SCHOOL_SEATS_PRICE_ID || 'prod_TQqEowVFMUF4i0');
        if (aIsSeats && !bIsSeats) return -1;
        if (!aIsSeats && bIsSeats) return 1;
        return 0;
      });
  });

  group$: Observable<Group | undefined> = new Observable();
  user: UserModel | null = null;
  currentPage: SubPages = SubPages.Products;
  exit$ = new Observable<void>();
  subPages = SubPages;

  groupSubscriptions: any[] = [];
  isSeatsSubscribed = false;

  constructor() {
    this.group$ = this.store.select(groupSelector.getSelectedGroup);

    this.store
      .select((state) => state.user.user)
      .pipe(take(1))
      .subscribe(async (user) => {
        this.user = user;
      });

    this.group$.subscribe((group) => {
      if (group) {
        this.groupType.set(group.groupType);
        this.groupCurrency.set((group.currency || 'usd').toLowerCase());
        const count = group.groupType === 'church' ? group.activeMembers : group.students;
        if (count !== undefined) {
          this.studentCount = count;
          this.updateSeatsSelection();
        }
      }
    });

    effect(() => {
      // Re-evaluate seats selection when products or billing configuration changes
      this.products();
      this.groupCurrency();
      this.billingInterval();
      this.updateSeatsSelection();
    });
  }

  getSeatsProduct(): Product | undefined {
    return this.getGroupProducts().find(p => {
      if (p.metadata?.productType === 'seats') return true;
      return p.id === (environment.STRIPE_SCHOOL_SEATS_PRICE_ID || 'prod_TQqEowVFMUF4i0');
    });
  }

  getSeatsProductId(): string {
    return this.getSeatsProduct()?.id || environment.STRIPE_SCHOOL_SEATS_PRICE_ID || 'prod_TQqEowVFMUF4i0';
  }

  getSeatsTierQuantity(studentCount: number): number {
    const seatsProduct = this.getSeatsProduct();
    const price = seatsProduct?.prices?.[0];
    if (price && price.tiers && price.tiers.length > 0) {
      for (const tier of price.tiers) {
        if (tier.up_to === null || studentCount <= tier.up_to) {
          return tier.up_to === null ? studentCount : tier.up_to;
        }
      }
    }
    return studentCount;
  }

  updateSeatsSelection() {
    const seatsProdId = this.getSeatsProductId();

    // Clean up other potential/stale seats product IDs in selectedQuantities to avoid duplicates
    this.getGroupProducts().forEach(p => {
      if (this.isSeatsProduct(p.id) && p.id !== seatsProdId) {
        delete this.selectedQuantities[p.id];
        this.selectedProductIds = this.selectedProductIds.filter(id => id !== p.id);
      }
    });

    if (this.studentCount > this.getFreeSeatsCount() && !this.isSeatsSubscribed) {
      const tierQty = this.getSeatsTierQuantity(this.studentCount);
      this.selectedQuantities[seatsProdId] = tierQty;
      if (!this.selectedProductIds.includes(seatsProdId)) {
        this.selectedProductIds.push(seatsProdId);
      }
    } else {
      this.selectedQuantities[seatsProdId] = 0;
      this.selectedProductIds = this.selectedProductIds.filter(id => id !== seatsProdId);
    }
  }

  async loadSubscriptions() {
    if (this.groupId()) {
      try {
        const subs = await this.stripeService.getGroupSubscriptions(this.groupId()!);
        this.groupSubscriptions = subs || [];
        this.isSeatsSubscribed = this.groupSubscriptions.some((sub) =>
          sub.subscription?.items?.data?.some((item: any) => {
            const prodId = typeof item.price.product === 'string' ? item.price.product : item.price.product?.id;
            return prodId && this.isSeatsProduct(prodId);
          })
        );
        this.updateSeatsSelection();
      } catch (err) {
        this.logger.error('Error loading group subscriptions:', err);
      }
    }
  }

  ngOnInit() {
    this.productService.refresh();
    if (this.groupId()) {
      this.store.dispatch(groupActions.getGroupStats({ groupId: this.groupId()! }));
      this.loadSubscriptions();
    }
  }

  isSeatsProduct(productId: string): boolean {
    const seatsId = environment.STRIPE_SCHOOL_SEATS_PRICE_ID || 'prod_TQqEowVFMUF4i0';
    if (productId === seatsId) return true;
    const product = this.getGroupProducts().find(p => p.id === productId);
    return product?.metadata?.productType === 'seats';
  }

  selectedQuantities: { [productId: string]: number } = {};

  getProductQuantity(productId: string): number {
    return this.selectedQuantities[productId] ?? 0;
  }

  onToggleProduct(product: Product) {
    const isSeats = this.isSeatsProduct(product.id);
    if (isSeats && this.studentCount > this.getFreeSeatsCount()) {
      // Seats product must be paid for if students count > 25, user cannot deselect it
      return;
    }
    if (this.getProductQuantity(product.id) > 0) {
      this.selectedQuantities[product.id] = 0;
    } else {
      this.selectedQuantities[product.id] = isSeats ? this.getSeatsTierQuantity(this.studentCount) : 1;
    }
    this.updateSelectedProductIds();
  }

  updateSelectedProductIds() {
    this.selectedProductIds = Object.keys(this.selectedQuantities).filter(
      (id) => this.selectedQuantities[id] > 0
    );
  }

  isSelected(productId: string) {
    return this.getProductQuantity(productId) > 0;
  }

  increaseQuantity(product: Product) {
    if (this.isSeatsProduct(product.id)) return;
    const current = this.getProductQuantity(product.id);
    if (current === 0) {
      this.selectedQuantities[product.id] = 1;
    } else {
      this.selectedQuantities[product.id] = current + 1;
    }
    this.updateSelectedProductIds();
  }

  decreaseQuantity(product: Product) {
    if (this.isSeatsProduct(product.id)) return;
    const current = this.getProductQuantity(product.id);
    if (current <= 1) {
      this.selectedQuantities[product.id] = 0;
    } else {
      this.selectedQuantities[product.id] = current - 1;
    }
    this.updateSelectedProductIds();
  }

  setQuantity(product: Product, event: Event) {
    if (this.isSeatsProduct(product.id)) return;
    const target = event.target as HTMLInputElement;
    const val = parseInt(target.value, 10);
    if (isNaN(val) || val <= 0) {
      this.selectedQuantities[product.id] = 0;
    } else {
      this.selectedQuantities[product.id] = val;
    }
    this.updateSelectedProductIds();
  }

  getCheckoutTotals(): { currency: string, subtotal: number, vat: number, total: number }[] {
    const selectedProds = this.products().filter(p => this.isSelected(p.id));
    const totalsByCurrency: { [currency: string]: { subtotal: number, vat: number, total: number } } = {};
    
    selectedProds.forEach(prod => {
      const price = prod.prices[0];
      const currency = price.currency.toUpperCase();
      
      const cost = this.getProductCost(prod);
      
      if (!totalsByCurrency[currency]) {
        totalsByCurrency[currency] = { subtotal: 0, vat: 0, total: 0 };
      }
      
      totalsByCurrency[currency].subtotal += cost;
    });
    
    return Object.keys(totalsByCurrency).map(curr => {
      const sub = totalsByCurrency[curr].subtotal;
      const vat = sub * 0.05; // 5% UAE standard VAT
      const tot = sub + vat;
      
      return {
        currency: curr,
        subtotal: sub,
        vat: vat,
        total: tot
      };
    });
  }

  getTotalCheckoutPrice(): number {
    const totals = this.getCheckoutTotals();
    return totals.reduce((sum, t) => sum + t.total, 0);
  }

  getProductCost(product: Product): number {
    const qty = this.getProductQuantity(product.id);
    const price = product.prices[0];
    if (!price) return 0;

    if (this.isPromoCodeApplicableTo(product.id)) {
      const details = this.appliedCouponDetails();
      if (details) {
        if (details.percent_off === 100) {
          return 0;
        }
        const cost = this.isSeatsProduct(product.id)
          ? this.calculateSeatsCost(qty, price)
          : ((price.unit_amount ?? 0) / 100) * qty;

        if (details.percent_off) {
          return cost * (1 - details.percent_off / 100);
        } else if (details.amount_off) {
          return Math.max(0, cost - details.amount_off / 100);
        }
      }
    }

    if (this.isSeatsProduct(product.id)) {
      return this.calculateSeatsCost(qty, price);
    }

    if (!price.tiers || price.tiers.length === 0) {
      return ((price.unit_amount ?? 0) / 100) * qty;
    }

    const tiersMode = price.tiers_mode || 'graduated';
    if (tiersMode === 'volume') {
      let matchedTier = price.tiers[price.tiers.length - 1];
      for (let i = 0; i < price.tiers.length; i++) {
        const tier = price.tiers[i];
        if (tier.up_to === null || qty <= tier.up_to) {
          matchedTier = tier;
          break;
        }
      }
      const unitAmt = (matchedTier.unit_amount ?? 0) / 100;
      const flatAmt = (matchedTier.flat_amount ?? 0) / 100;
      return qty * unitAmt + flatAmt;
    } else {
      let cost = 0;
      let remaining = qty;
      let prevUpTo = 0;

      for (let i = 0; i < price.tiers.length; i++) {
        const tier = price.tiers[i];
        const upTo = tier.up_to;
        const tierLimit = upTo === null ? Infinity : (upTo - prevUpTo);
        const qtyInTier = Math.min(remaining, tierLimit);

        if (qtyInTier <= 0) break;

        const unitAmt = (tier.unit_amount ?? 0) / 100;
        const flatAmt = (tier.flat_amount ?? 0) / 100;
        cost += qtyInTier * unitAmt + flatAmt;

        remaining -= qtyInTier;
        prevUpTo = upTo ?? Infinity;
      }

      return cost;
    }
  }

  calculateSeatsCost(count: number, priceObj?: any): number {
    let price = priceObj;
    if (!price) {
      const seatsProduct = this.getSeatsProduct();
      if (seatsProduct && seatsProduct.prices) {
        const currency = (this.groupCurrency() || 'usd').toLowerCase();
        const selectedInterval = this.billingInterval();
        
        let filteredPrices = seatsProduct.prices.filter(
          (p) => p.active && p.currency.toLowerCase() === currency
        );
        if (filteredPrices.length === 0 && currency !== 'usd') {
          filteredPrices = seatsProduct.prices.filter(
            (p) => p.active && p.currency.toLowerCase() === 'usd'
          );
        }
        let intervalPrices = filteredPrices.filter(
          (p) => p.recurring?.interval === selectedInterval
        );
        if (intervalPrices.length === 0) {
          intervalPrices = filteredPrices;
        }
        price = intervalPrices[0];
      }
    }

    if (!price || !price.tiers || price.tiers.length === 0) {
      if (price) {
        return ((price.unit_amount ?? 0) / 100) * count;
      }
      return 0;
    }

    const tiersMode = price.tiers_mode || 'graduated';
    if (tiersMode === 'volume') {
      let matchedTier = price.tiers[price.tiers.length - 1];
      for (let i = 0; i < price.tiers.length; i++) {
        const tier = price.tiers[i];
        if (tier.up_to === null || count <= tier.up_to) {
          matchedTier = tier;
          break;
        }
      }
      const unitAmt = (matchedTier.unit_amount ?? 0) / 100;
      const flatAmt = (matchedTier.flat_amount ?? 0) / 100;
      return count * unitAmt + flatAmt;
    }

    let cost = 0;
    let remaining = count;
    let prevUpTo = 0;

    for (let i = 0; i < price.tiers.length; i++) {
      const tier = price.tiers[i];
      const upTo = tier.up_to;
      const tierLimit = upTo === null ? Infinity : (upTo - prevUpTo);
      const qtyInTier = Math.min(remaining, tierLimit);

      if (qtyInTier <= 0) break;

      const unitAmt = (tier.unit_amount ?? 0) / 100;
      const flatAmt = (tier.flat_amount ?? 0) / 100;
      cost += qtyInTier * unitAmt + flatAmt;

      remaining -= qtyInTier;
      prevUpTo = upTo ?? Infinity;
    }

    return cost;
  }

  async checkExistingSubscription() {
    this.hasExistingSubscription = false;
    this.existingSubscriptionId = null;
    this.useExistingCard = false;
  }

  async proceedToPayment() {
    if (this.selectedProductIds.length === 0) {
      this.error.set('Please select at least one product.');
      return;
    }
    this.error.set(null);
    this.processing = true;
    try {
      await this.checkExistingSubscription();
      this.currentPage = SubPages.Payment;
      if (!this.useExistingCard) {
        setTimeout(() => {
          this.initStripeElements();
        }, 0);
      }
    } catch (err: any) {
      this.error.set(err.message || 'Unexpected payment error');
    } finally {
      this.processing = false;
    }
  }

  formatPrice(amount: number, currency: string): string {
    const code = currency.toLowerCase();
    if (code === 'usd') {
      return `$${amount.toFixed(2)}`;
    }
    if (code === 'eur') {
      return `€${amount.toFixed(2)}`;
    }
    if (code === 'gbp') {
      return `£${amount.toFixed(2)}`;
    }
    if (code === 'aed') {
      return `AED ${amount.toFixed(2)}`;
    }
    return `${amount.toFixed(2)} ${currency.toUpperCase()}`;
  }

  onToggleUseExistingCard() {
    this.useExistingCard = !this.useExistingCard;
    if (!this.useExistingCard && !this.clientSecret()) {
      setTimeout(() => {
        this.initStripeElements();
      }, 0);
    }
  }

  async initStripeElements() {
    this.loadingPayment.set(true);
    this.error.set(null);
    try {
      const createSetupIntentFn = httpsCallable(
        this.functions,
        environment.FUNCTION_STRIPE_CREATE_INTENT,
        { limitedUseAppCheckTokens: true }
      );

      const result: any = await createSetupIntentFn({
        name: this.user?.name,
        email: this.user?.email,
        groupId: this.groupId(),
      });

      if (!result?.data?.clientSecret) {
        this.error.set('Failed to initialize setup intent');
        this.loadingPayment.set(false);
        return;
      }

      this.clientSecret.set(result.data.clientSecret);

      this.stripe = await loadStripe(environment.STRIPE_PUBLISHABLE_KEY);
      if (!this.stripe) {
        this.error.set('Stripe failed to initialize');
        this.loadingPayment.set(false);
        return;
      }

      this.elements = this.stripe.elements({
        clientSecret: result.data.clientSecret,
      });

      const paymentElement = this.elements.create('payment');
      paymentElement.mount('#payment-element');

      this.loadingPayment.set(false);
    } catch (err: any) {
      console.error(err);
      this.error.set(err.message ?? 'Unexpected error');
      this.loadingPayment.set(false);
    }
  }

  async confirmSubscription() {
    this.loadingPayment.set(true);
    this.error.set(null);
    this.processing = true;

    try {
      const items = this.products()
        .filter(p => this.isSelected(p.id))
        .map(p => ({
          priceId: p.prices[0].id,
          quantity: this.getProductQuantity(p.id)
        }));

      if (items.length === 0) {
        this.error.set('Please select at least one product.');
        this.loadingPayment.set(false);
        this.processing = false;
        return;
      }

      if (this.useExistingCard) {
        await this.stripeService.createSubscription(
          this.groupId()!,
          null,
          undefined,
          undefined,
          items,
          this.promoCodeApplied() ? (this.promoCode() || undefined) : undefined
        );
        this.success.set(true);
        this.showPaymentModal.set(false);
        setTimeout(() => {
          this.router.navigate(['..'], { relativeTo: this.route });
        }, 1500);
      } else {
        if (!this.stripe || !this.elements) {
          this.error.set('Stripe elements not loaded');
          this.loadingPayment.set(false);
          this.processing = false;
          return;
        }

        const { error, setupIntent } = await this.stripe.confirmSetup({
          elements: this.elements,
          confirmParams: {
            return_url: window.location.origin + `/school/${this.groupId()}/billing`,
          },
          redirect: 'if_required',
        });

        if (error) {
          this.error.set(error.message || 'Payment failed');
          this.loadingPayment.set(false);
          this.processing = false;
          return;
        }

        if (setupIntent?.status === 'succeeded') {
          await this.stripeService.createSubscription(
            this.groupId()!,
            setupIntent.id,
            undefined,
            undefined,
            items,
            this.promoCodeApplied() ? (this.promoCode() || undefined) : undefined
          );
          this.success.set(true);
          this.showPaymentModal.set(false);
          setTimeout(() => {
            this.router.navigate(['..'], { relativeTo: this.route });
          }, 1500);
        }
      }
    } catch (err: any) {
      console.error(err);
      this.error.set(err.message || 'Unexpected payment error');
    } finally {
      this.loadingPayment.set(false);
      this.processing = false;
    }
  }

  goBack() {
    if (this.currentPage === SubPages.Payment) {
      this.currentPage = SubPages.Products;
    } else {
      this.router.navigate(['..'], { relativeTo: this.route });
    }
  }

  onPromoCodeChange(value: string) {
    this.promoCode.set(value.trim());
    this.promoCodeError.set(null);
  }

  async applyPromoCode() {
    const code = this.promoCode().trim();
    if (!code) return;

    this.validatingPromoCode.set(true);
    this.promoCodeError.set(null);

    try {
      const validatePromoFn = httpsCallable(this.functions, 'payment_validatePromoCode', {
        limitedUseAppCheckTokens: true,
      });
      const result: any = await validatePromoFn({ promoCode: code });

      if (result?.data?.valid) {
        this.promoCodeApplied.set(true);
        this.appliedCouponDetails.set(result.data);
      } else {
        this.promoCodeError.set(result?.data?.message || 'Invalid promotion or voucher code.');
      }
    } catch (err: any) {
      console.error(err);
      const isInternal =
        err?.message === 'internal' ||
        err?.code === 'functions/internal' ||
        err?.code === 'functions/not-found';
      const message = isInternal
        ? 'Voucher service is temporarily unavailable or code is invalid. Please try again.'
        : (err?.message || 'Error validating promo code.');
      this.promoCodeError.set(message);
    } finally {
      this.validatingPromoCode.set(false);
    }
  }

  clearPromoCode() {
    this.promoCode.set('');
    this.promoCodeApplied.set(false);
    this.appliedCouponDetails.set(null);
    this.promoCodeError.set(null);
  }

  isPromoCodeApplicableTo(productId: string): boolean {
    const details = this.appliedCouponDetails();
    if (!details) return false;

    if (details.applies_to?.products && Array.isArray(details.applies_to.products) && details.applies_to.products.length > 0) {
      return details.applies_to.products.includes(productId) || this.isSeatsProduct(productId);
    }

    return true;
  }
}

enum SubPages {
  Products = 'products',
  Subscriptions = 'subscriptions',
  Payment = 'payment',
}
